import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import pieChartLayout from '../helpers/pieChartLayout'

/**
 * Picks the pie layout from the chart's own width. Needed because recharts
 * takes radius and legend position as props, which CSS media queries cannot
 * reach — and the container width, not the window, decides (a report column
 * can be narrow on a wide screen too).
 *
 * Measures once on mount, before paint, so a phone never flashes the wide
 * layout: ResponsiveContainer's `onResize` only fires from its ResizeObserver,
 * i.e. after the first paint (and not at all in a hidden tab). After that,
 * `onResize` keeps it current.
 *
 * @param {number} itemCount - Number of legend items
 * @returns {{
 *   layout: import('../helpers/pieChartLayout').PieChartLayout,
 *   onResize: (width: number, height: number) => void,
 *   ref: React.RefObject<HTMLDivElement>,
 * }}
 */
const usePieChartLayout = (itemCount) => {
    /** @type {React.RefObject<HTMLDivElement>} */
    const ref = useRef(null)
    const [width, setWidth] = useState(/** @type {number|null} */ (null))

    useLayoutEffect(() => {
        if (ref.current) setWidth(ref.current.getBoundingClientRect().width)
    }, [])

    const onResize = useCallback((w) => setWidth(w), [])
    return { layout: pieChartLayout(width, itemCount), onResize, ref }
}

export default usePieChartLayout
